import { Stack } from 'component-library';

import { IndicatorName } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { CanopyCoverHistoricalChart } from '@/components/Charts';
import { ItemizedGraphTypeRenderer } from '@/components/Charts/components/ItemizedGraphType';
import { NUM_ITEMS_TYPE } from '@/components/Charts/hooks/usePlotsForItemizedGraph';
import { GraphFact } from '@/components/Charts/types';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { useNCCardContext } from '@/components/NCCard/NCCard';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useScaledCustomBenchmark } from '@/hooks/useScaledCustomBenchmark';
import { useOrganizationIndicatorGraphFact } from '@/pages/shared/hooks/useOrganizationFact';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';

import { NoData } from '../../../../components/NoData';

export const CanopyCoverGraphTile = ({
  zoom,
  setZoom,
  plotGroupId,
  graphType,
  selectedBenchmark,
  ...props
}: Partial<GraphTileProps> & { selectedBenchmark?: string | null }) => {
  const canopyCoverHistoricFact = IndicatorName.canopy_cover_pct_graph;
  const { [canopyCoverHistoricFact]: canopyCoverHistoric } = useOrganizationIndicatorGraphFact<GraphFact[]>(
    canopyCoverHistoricFact,
    null,
    undefined,
    plotGroupId,
  );
  const chartData = canopyCoverHistoric?.value;

  const noChartData = !chartData?.length;

  const organizationId = useOrganizationId();
  const indicator = GraphTileType.canopy_cover;
  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, indicator);
  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmark);
  const { analysisType } = useNCCardContext();
  const isTotal = analysisType === 'total';
  const displayCustomBenchmark = useScaledCustomBenchmark(activeCustomBenchmark, isTotal);

  if (noChartData) {
    return <NoData />;
  }

  return (
    <Stack center data-testid='canopy-cover-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-8 lg:pl-1'>
        <ItemizedGraphTypeRenderer
          graphType={graphType}
          indicator={GraphTileType.canopy_cover}
          plotRenderType={props?.plotRenderType ?? NUM_ITEMS_TYPE.TOP_PLOTS}
          plotGroupId={plotGroupId ?? undefined}
          customBenchmark={displayCustomBenchmark}
          defaultGraphType={
            <CanopyCoverHistoricalChart
              data={chartData}
              customBenchmark={displayCustomBenchmark}
              showTooltip={true}
              data-testid='canopy-cover-historic-chart'
              zoom={zoom}
              setZoom={setZoom}
            />
          }
        />
      </Stack>
    </Stack>
  );
};
